import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button, Spinner } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaFileExcel, FaBed } from "react-icons/fa";
import pythonApiClient from "../../../services/api/pythonBaseApi";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";

const norm = (s) => (s || "").toString().trim().toLowerCase();
const esCamaReservadaUOcupada = (estadoNombre) => {
    const n = norm(estadoNombre);
    return n.includes("reserv") || n.includes("ocup");
};

const fmtFechaHoraPe = (v) => {
    if (v == null || v === "") return "";
    const s = String(v).trim();
    if (!s) return "";
    const d =
        s.includes("T") && !s.includes(" ")
            ? new Date(s)
            : new Date(s.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString("es-PE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const etiquetaActivo = (v) => {
    if (v === 0 || v === false || v === "0") return "Inactiva";
    return "Activa";
};

const fmtFechaPe = (v) => {
    if (v == null || v === "") return "";
    const s = String(v).trim();
    if (!s) return "";
    const soloFecha = s.length >= 10 ? s.slice(0, 10) : s;
    const d = new Date(`${soloFecha}T00:00:00`);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString("es-PE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    });
};

const ReporteCamas = () => {
    const navigate = useNavigate();
    const selectedProject = JSON.parse(localStorage.getItem("selectedProject") || "null");

    const [loading, setLoading] = useState(false);
    const [zonas, setZonas] = useState([]);
    const [modulos, setModulos] = useState([]);

    const [filtros, setFiltros] = useState({
        proyecto_id: selectedProject?.id || null,
        zona_id: "",
        modulo_id: "",
        tipo_habitacion_id: "",
        estado_cama_id: "",
        solo_zonas_activas: false,
        solo_camas_activas: false,
    });

    // Cargar zonas al inicio
    // Cargar zonas al inicio
    useEffect(() => {
        if (selectedProject?.id) {
            setFiltros(prev => ({ ...prev, proyecto_id: selectedProject.id }));
            loadZonas();
        }
    }, [selectedProject?.id]);

    // Cargar módulos cuando cambia la zona
    useEffect(() => {
        if (filtros.zona_id) {
            loadModulos(filtros.zona_id);
        } else {
            setModulos([]);
            setFiltros(prev => ({ ...prev, modulo_id: "" }));
        }
    }, [filtros.zona_id]);

    const loadZonas = async () => {
        try {
            const response = await pythonApiClient.get("/mantenimientos/zonas", {
                params: { proyecto_id: selectedProject.id },
                headers: { "X-Proyecto-ID": selectedProject.id }
            });
            setZonas(response.data || []);
        } catch (error) {
            console.error("Error cargando zonas:", error);
        }
    };

    const loadModulos = async (zonaId) => {
        try {
            const response = await pythonApiClient.get("/mantenimientos/modulos", {
                params: { zona_id: zonaId },
                headers: { "X-Proyecto-ID": selectedProject.id }
            });
            setModulos(response.data || []);
        } catch (error) {
            console.error("Error cargando módulos:", error);
        }
    };

    const handleGenerarReporte = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const params = { ...filtros };
            // Limpiar filtros vacíos (los booleanos se envían siempre)
            Object.keys(params).forEach(key => {
                if (params[key] === "") delete params[key];
            });
            params.solo_zonas_activas = !!filtros.solo_zonas_activas;
            params.solo_camas_activas = !!filtros.solo_camas_activas;

            const response = await pythonApiClient.get("/reportes/hoteleria/camas", {
                params,
                headers: { "X-Proyecto-ID": selectedProject.id }
            });

            if (response.data.success) {
                const camas = response.data.camas || [];
                
                if (camas.length === 0) {
                    toast.warning("No se encontraron camas con los filtros seleccionados");
                    setLoading(false);
                    return;
                }

                // Generar Excel directamente
                const datosExcel = camas.map((c) => {
                    const showTrab = esCamaReservadaUOcupada(c.estado);
                    return {
                        "ID": c.id,
                        "ID Proveedor": c.id_proveedor ?? "",
                        "Código Proveedor": c.codigo_proveedor ?? "",
                        "Zona": c.zona,
                        "Módulo": c.modulo,
                        "Habitación": c.habitacion,
                        "Cama": c.cama,
                        "Estado Cama": c.estado,
                        "Cama activa": etiquetaActivo(c.cama_activo),
                        "Zona activa": etiquetaActivo(c.zona_activo),
                        "Inicio estadía programado": showTrab ? fmtFechaPe(c.fecha_inicio_estadia) : "",
                        "Fin estadía programado": showTrab ? fmtFechaPe(c.fecha_fin_estadia) : "",
                        "Fecha reserva (registro)": showTrab ? fmtFechaHoraPe(c.fecha_reserva) : "",
                        "Fecha check-in": showTrab ? fmtFechaHoraPe(c.fecha_check_in) : "",
                        "DNI trabajador": showTrab ? (c.trabajador_dni ?? "") : "",
                        "Nombre y apellidos": showTrab ? (c.trabajador_nombre_apellidos ?? "") : "",
                    };
                });

                const wb = XLSX.utils.book_new();
                const ws = XLSX.utils.json_to_sheet(datosExcel);

                const colWidths = [
                    { wch: 8 }, { wch: 38 }, { wch: 16 }, { wch: 20 }, { wch: 20 },
                    { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 14 }, { wch: 14 },
                    { wch: 24 }, { wch: 22 }, { wch: 22 }, { wch: 18 },
                    { wch: 14 }, { wch: 28 },
                ];
                ws["!cols"] = colWidths;

                XLSX.utils.book_append_sheet(wb, ws, "Camas");
                const fecha = new Date().toISOString().split("T")[0];
                XLSX.writeFile(wb, `reporte_camas_${fecha}.xlsx`);
                
                toast.success(`Reporte generado exitosamente: ${response.data.total} camas exportadas`);
            } else {
                toast.error(response.data.message || "Error al generar el reporte");
            }
        } catch (error) {
            console.error("Error al generar reporte:", error);
            toast.error(error.response?.data?.detail || "Error al generar el reporte");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFiltros(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    return (
        <>
            <style>
                {`
          .dh-reporte-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
            padding: 2rem 0;
          }
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
            margin-bottom: 2rem;
          }
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.25rem 1.5rem;
            border: none;
            font-weight: 700;
          }
          .dh-card-header-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 10px 20px;
            font-weight: 600;
          }
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 10px 20px;
            font-weight: 600;
          }
          .btn-dh-success {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 10px 20px;
            font-weight: 600;
          }
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
          }
           .dh-table thead {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
        `}
            </style>

            <div className="dh-reporte-bg">
                <ToastContainer />
                <Container fluid>
                    <Row>
                        <Col>
                            <Card className="dh-card">
                                <Card.Header className="dh-card-header dh-card-header-primary">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="d-flex align-items-center">
                                            <FaBed className="me-3" size={24} />
                                            <h5 className="mb-0">Reporte de Camas</h5>
                                        </div>
                                        <Button className="btn-dh-secondary" onClick={() => navigate("/reportes")}>
                                            <FaArrowLeft className="me-2" /> Volver
                                        </Button>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Form onSubmit={handleGenerarReporte}>
                                        <Row className="mb-4">

                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Zona</Form.Label>
                                                    <Form.Select
                                                        name="zona_id"
                                                        value={filtros.zona_id}
                                                        onChange={handleInputChange}
                                                        className="dh-form-control"
                                                    >
                                                        <option value="">Todas</option>
                                                        {zonas.map(z => (
                                                            <option key={z.id} value={z.id}>
                                                                {z.nombre}{z.activo === false ? " (inactiva)" : ""}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>

                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Módulo</Form.Label>
                                                    <Form.Select
                                                        name="modulo_id"
                                                        value={filtros.modulo_id}
                                                        onChange={handleInputChange}
                                                        className="dh-form-control"
                                                    >
                                                        <option value="">Todos</option>
                                                        {modulos.map(m => (
                                                            <option key={m.id} value={m.id}>{m.nombre}</option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>

                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Tipo Habitación</Form.Label>
                                                    <Form.Select
                                                        name="tipo_habitacion_id"
                                                        value={filtros.tipo_habitacion_id}
                                                        onChange={handleInputChange}
                                                        className="dh-form-control"
                                                    >
                                                        <option value="">Todos</option>
                                                        <option value="1">Individual</option>
                                                        <option value="2">Doble</option>
                                                        <option value="3">Triple</option>
                                                        <option value="4">Cuadruple</option>
                                                        <option value="5">Camarote</option>
                                                        <option value="6">Quintuple</option>
                                                        <option value="7">Sextuple</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>

                                            <Col md={3}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Estado Cama</Form.Label>
                                                    <Form.Select
                                                        name="estado_cama_id"
                                                        value={filtros.estado_cama_id}
                                                        onChange={handleInputChange}
                                                        className="dh-form-control"
                                                    >
                                                        <option value="">Todos</option>
                                                        <option value="1">Libre</option>
                                                        <option value="2">Ocupada</option>
                                                        <option value="3">Averiada</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Check
                                                    type="checkbox"
                                                    id="solo_zonas_activas"
                                                    name="solo_zonas_activas"
                                                    label="Solo incluir camas de zonas activas"
                                                    checked={filtros.solo_zonas_activas}
                                                    onChange={handleInputChange}
                                                />
                                            </Col>
                                            <Col md={6}>
                                                <Form.Check
                                                    type="checkbox"
                                                    id="solo_camas_activas"
                                                    name="solo_camas_activas"
                                                    label="Solo incluir camas activas"
                                                    checked={filtros.solo_camas_activas}
                                                    onChange={handleInputChange}
                                                />
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col className="d-flex justify-content-end gap-3">
                                                <Button type="submit" className="btn-dh-primary" disabled={loading}>
                                                    {loading ? (
                                                        <>
                                                            <Spinner size="sm" className="me-2" />
                                                            Generando...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FaFileExcel className="me-2" />
                                                            Generar Reporte Excel
                                                        </>
                                                    )}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Form>

                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </>
    );
};

export default ReporteCamas;
